const ISO_DATE_FORMAT = /^(\d{4})-(\d{2})-(\d{2})$/

export enum DaysOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export function createDate(year: string, month: string, day: string): Date | undefined {
  const dayInt = Number.parseInt(day, 10)
  const monthInt = Number.parseInt(month, 10)
  const yearInt = Number.parseInt(year, 10)

  const isValid
    = Number.isInteger(yearInt) // all parts should be integers
      && Number.isInteger(monthInt)
      && Number.isInteger(dayInt)
      && monthInt > 0 // month must be 1-12
      && monthInt <= 12
      && dayInt > 0 // day must be 1-31
      && dayInt <= 31
      && yearInt > 0

  if (isValid) {
    const date = new Date(yearInt, monthInt - 1, dayInt)
    // this handles values where year is 0-99, which JS "helpfully" interprets as 1900-1999
    date.setFullYear(yearInt)

    return date
  }

  return undefined
}

/**
 * @param value date string in ISO format YYYY-MM-DD
 */
export function parseISODate(value: string): Date | undefined {
  if (!value) {
    return undefined
  }

  const matches = value.match(ISO_DATE_FORMAT)

  if (matches) {
    return createDate(matches[1], matches[2], matches[3])
  }

  return undefined
}

/**
 * print date in format YYYY-MM-DD
 * @param date
 */
export function printISODate(date: Date): string {
  if (!date) {
    return ''
  }

  const d = date.getDate().toString(10)
  const m = (date.getMonth() + 1).toString(10)
  const y = date.getFullYear().toString(10)

  return `${y.padStart(4, '0')}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}

function today() {
  const date = new Date()
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0))
}

export function getDayNames(locale: string, weekday: Exclude<Intl.DateTimeFormatOptions['weekday'], undefined>) {
  const days = []
  const options = { weekday }
  const day = today()

  for (let i = 0; i < 7; i++) {
    days[day.getDay()] = day.toLocaleDateString(locale, options)
    day.setDate(day.getDate() + 1)
  }

  return days
}

export function getMonthNames(locale: string, month: Exclude<Intl.DateTimeFormatOptions['month'], undefined>) {
  const months = []
  const options = { month }

  // set to first of the month, so that all dates are valid
  const day = today()
  day.setDate(1)

  for (let i = 0; i < 12; i++) {
    months[day.getMonth()] = day.toLocaleDateString(locale, options)
    day.setMonth(day.getMonth() + 1)
  }

  return months
}

/**
 * Compare if two dates are in the same month of the same year.
 */
export function isEqualMonth(a?: Date, b?: Date): boolean {
  if (a == null || b == null) {
    return false
  }

  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

/**
 * Compare if two dates are equal in terms of day, month, and year
 */
export function isEqual(a?: Date, b?: Date): boolean {
  if (a == null || b == null) {
    return false
  }

  return isEqualMonth(a, b) && a.getDate() === b.getDate()
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

export function addMonths(date: Date, months: number): Date {
  const d = new Date(date)
  d.setMonth(date.getMonth() + months)
  return d
}

export function addYears(date: Date, years: number): Date {
  const d = new Date(date)
  d.setFullYear(date.getFullYear() + years)
  return d
}

export function startOfWeek(date: Date, firstDayOfWeek: DaysOfWeek = DaysOfWeek.Monday): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = (day < firstDayOfWeek ? 7 : 0) + day - firstDayOfWeek

  d.setDate(d.getDate() - diff)
  return d
}

export function endOfWeek(date: Date, firstDayOfWeek: DaysOfWeek = DaysOfWeek.Monday): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = (day < firstDayOfWeek ? -7 : 0) + 6 - (day - firstDayOfWeek)

  d.setDate(d.getDate() + diff)
  return d
}

const MS_PER_DAY = 86_400_000

/**
 * ISO-8601 week-of-year: week 1 is the week containing the year's first
 * Thursday, so a year has 52 or 53 weeks.
 *
 * The number comes from the Thursday inside the week and the year is read from
 * that day too, which is what resolves both directions across a year boundary:
 * the week of 29 December 2025 is week 1 (of 2026), and the week of 1 January
 * 2023 is week 52 (of 2022).
 *
 * A non-Monday `firstDayOfWeek` shifts where a displayed row starts and ends,
 * but not which ISO week that row reports: the Thursday is located within the
 * row, so a Sunday-started row spanning 30 August to 5 September 2026 is still
 * week 36. Numbering the shifted row on its own terms instead would put it one
 * off every other calendar the user reads.
 */
export function getWeekNumber(date: Date, firstDayOfWeek: DaysOfWeek = DaysOfWeek.Monday): number {
  const weekStart = startOfWeek(date, firstDayOfWeek)
  const midweek = addDays(weekStart, (DaysOfWeek.Thursday - weekStart.getDay() + 7) % 7)
  // Compare midnight to midnight so the caller's time of day cannot round into
  // the next day; Math.round then only absorbs a DST transition between
  // 1 January and the midweek day.
  midweek.setHours(0, 0, 0, 0)
  const dayOfYear = Math.round((midweek.getTime() - startOfYear(midweek).getTime()) / MS_PER_DAY) + 1

  return Math.ceil(dayOfYear / 7)
}

export function startOfMonth(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), 1)
  d.setFullYear(date.getFullYear())
  return d
}

export function endOfMonth(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  d.setFullYear(date.getFullYear())
  return d
}

export function setMonth(date: Date, month: number): Date {
  const d = new Date(date)
  d.setMonth(month)
  return d
}

export function setYear(date: Date, year: number): Date {
  const d = new Date(date)
  d.setFullYear(year)
  return d
}

export function startOfYear(date: Date): Date {
  const d = new Date(date.getFullYear(), 0, 1)
  d.setFullYear(date.getFullYear())
  return d
}

export function endOfYear(date: Date): Date {
  const d = new Date(date.getFullYear(), 11, 31)
  d.setFullYear(date.getFullYear())
  return d
}

/** The calendar quarter (1–4) a date falls in. */
export function getQuarter(date: Date): number {
  return Math.floor(date.getMonth() / 3) + 1
}

export function startOfQuarter(date: Date): Date {
  const month = (getQuarter(date) - 1) * 3
  const d = new Date(date.getFullYear(), month, 1)
  d.setFullYear(date.getFullYear())
  return d
}

export function endOfQuarter(date: Date): Date {
  const month = (getQuarter(date) - 1) * 3
  // Day 0 of the month after the quarter's last month is that month's last day.
  const d = new Date(date.getFullYear(), month + 3, 0)
  d.setFullYear(date.getFullYear())
  return d
}

/** The inclusive `[start, end]` of quarter `quarter` (1–4) in calendar `year`. */
export function getQuarterRange(quarter: number, year: number): [Date, Date] {
  const startMonth = (quarter - 1) * 3
  const start = new Date(year, startMonth, 1)
  start.setFullYear(year)
  const end = new Date(year, startMonth + 3, 0)
  end.setFullYear(year)
  return [start, end]
}

/**
 * Whether quarter `quarter` (1–4) of the current year (relative to `today`) is
 * fully in the past — used to hide not-yet-started current-year quarter presets.
 */
export function isQuarterCompleted(quarter: number, today: Date): boolean {
  return quarter < getQuarter(today)
}

/**
 * Ensures date is within range, returns min or max if out of bounds
 */
export function clamp(date: Date, min?: Date, max?: Date): Date {
  const time = date.getTime()

  if (min && min instanceof Date && time < min.getTime()) {
    return min
  }

  if (max && max instanceof Date && time > max.getTime()) {
    return max
  }

  return date
}

/**
 * Check if date is within a min and max
 */
export function inRange(date: Date, min?: Date, max?: Date): boolean {
  return clamp(date, min, max) === date
}

/**
 * given start and end date, return an (inclusive) array of all dates in between
 * @param start
 * @param end
 */
function getDaysInRange(start: Date, end: Date): Date[] {
  const days: Date[] = []
  let current = start

  while (!isEqual(current, end)) {
    days.push(current)
    current = addDays(current, 1)
  }

  days.push(current)

  return days
}

/**
 * given a date, return an array of dates from a calendar perspective
 * @param date
 * @param firstDayOfWeek
 */
export function getViewOfMonth(date: Date, firstDayOfWeek: DaysOfWeek = DaysOfWeek.Monday): Date[] {
  const start = startOfWeek(startOfMonth(date), firstDayOfWeek)
  const end = endOfWeek(endOfMonth(date), firstDayOfWeek)

  return getDaysInRange(start, end)
}
